import { Button } from '@ps-design-system/components/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@ps-design-system/components/Dialog';
import { AddOnCounter, getCategoryString } from '@ps-design-system/components/AddOnCounter';
import { Spinner } from '@ps-design-system/components/Spinner';
import { Text } from '@ps-design-system/components/Text';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

const groupInventoryByCategory = (inventory) => {
  return (inventory || []).reduce((acc, item) => {
    if (!acc[item.categoryId]) {
      acc[item.categoryId] = [];
    }

    acc[item.categoryId].push(item);

    return acc;
  }, {});
};

export const AddOnsDialog = injectIntl(
  ({
    items,
    watchFormValues,
    addOnsTotalCost,
    currency,
    intl,
    isFetchingQuote,
    disabled,
    isMobile
  }) => {
    const categoryTabs = useMemo(() => {
      const groupedItems = groupInventoryByCategory(items);
      return Object.keys(groupedItems)
        .sort()
        .map((category) => {
          return {
            label: getCategoryString(intl, category),
            items: groupedItems[category]
          };
        });
    }, [items]);

    const [selectedCategory, setSelectedCategory] = useState(categoryTabs[0]);

    const [isOpen, setIsOpen] = useState(false);

    const addOnsFormValues = watchFormValues('addOns');

    const totalSelectedAddOns = Object.values(addOnsFormValues || {}).reduce(
      (acc, value) => acc + value,
      0
    );

    return (
      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
        data-testingid="add-ons-dialog"
      >
        <DialogTrigger asChild>
          <Button appearance="white" variant="outline-1" fullWidth={isMobile}>
            <FormattedMessage
              id="addons_dialog.see_all_addons"
              defaultMessage="See all add-ons{totalItems}"
              values={{
                totalItems: isMobile ? ` (${(items || []).length})` : ''
              }}
            />
          </Button>
        </DialogTrigger>

        <DialogContent
          style={{
            maxWidth: '800px',
            maxHeight: isMobile ? '100vh' : '75vh',
            height: isMobile ? '100dvh' : '560px',
            gap: '0px'
          }}
        >
          <DialogHeader
            showCloseButton={true}
            style={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.20)',
              position: 'relative'
            }}
          >
            <DialogTitle style={{ margin: '0px' }}>
              <Text
                typeface="headline-4-m"
                style={{ textAlign: 'left', display: 'block' }}
              >
                <FormattedMessage
                  id="addons_dialog.select_addons"
                  defaultMessage="Select add-ons"
                />
              </Text>
            </DialogTitle>

            <div style={{ height: '32px' }} />
            <div
              style={{
                display: 'flex',
                gap: '32px',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                padding: isMobile ? '0 24px' : '0 32px',
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0'
              }}
            >
              {categoryTabs.map((category) => {
                return (
                  <div
                    key={category.label}
                    style={{ position: 'relative', paddingBottom: '8px' }}
                  >
                    <Button
                      variant="text-only"
                      onClick={() => setSelectedCategory(category)}
                      style={{
                        color: '#121212',
                        outline: 'none',
                        opacity:
                          selectedCategory.label === category.label ? 1 : 0.5
                      }}
                    >
                      {category.label}
                    </Button>
                    <span
                      style={{
                        height: '2px',
                        position: 'absolute',
                        width: '100%',
                        left: '0',
                        bottom: '0',
                        backgroundColor:
                          selectedCategory.label === category.label
                            ? '#121212'
                            : 'transparent'
                      }}
                    ></span>
                  </div>
                );
              })}
            </div>
          </DialogHeader>

          <DialogBody isScrollable={true}>
            <div
              style={{
                padding: isMobile ? '16px 0px' : '32px 0px'
              }}
            >
              <Text
                typeface="headline-2-m"
                element="p"
                style={{ margin: 0, marginBottom: '16px' }}
              >
                {selectedCategory.label}
              </Text>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}
              >
                {selectedCategory.items.map((item) => {
                  return (
                    <AddOnCounter
                      key={item.id}
                      addOnItem={item}
                      name={`addOns.${item.id}`}
                      testId={`add-ons-${item.id}`}
                      intl={intl}
                      disabled={disabled}
                    />
                  );
                })}
              </div>
            </div>
          </DialogBody>

          <DialogFooter
            style={{
              padding: isMobile ? '16px' : '32px',
              borderTop: '1px solid rgba(0, 0, 0, 0.20)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Text style={isMobile ? { order: 1, marginBottom: '16px' } : {}}>
              <FormattedMessage
                id="addons_dialog.total"
                defaultMessage="{totalAddOns} items selected {totalCost}"
                values={{
                  totalAddOns: totalSelectedAddOns,
                  totalCost: isFetchingQuote ? (
                    <Spinner appearance="black" style={{ marginLeft: '4px' }} />
                  ) : (
                    addOnsTotalCost > 0 &&
                    `(${intl.formatNumber(addOnsTotalCost || 0, {
                      style: 'currency',
                      currency
                    })})`
                  )
                }}
              >
                {(...content) => (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {content}
                  </span>
                )}
              </FormattedMessage>
            </Text>
            <Button onClick={() => setIsOpen(false)} fullWidth={isMobile}>
              <FormattedMessage id="addons_dialog.done" defaultMessage="Done" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

AddOnsDialog.displayName = 'AddOnsDialog';
AddOnsDialog.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  addOnsTotalCost: PropTypes.number,
  currency: PropTypes.string,
  isFetchingQuote: PropTypes.bool,
  isMobile: PropTypes.bool,
  intl: PropTypes.object
};
